<template>
  <div>
    <v-dialog
      v-model="show"
      width="90%"
      max-width="600"
      scrollable
    >
      <v-card>
        <v-card-title>
          Departments
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            dense
            class="mr-4"
          ></v-text-field>
          <v-btn
            icon
            @click.prevent="show = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-3">
          <v-data-table
            dense
            fixed-header
            :headers="tableHeaders"
            :items="departments"
            :search="search"
            :items-per-page="10"
            :footer-props="{
              'items-per-page-options': [10, 20, 50, -1]
            }"
          >
            <template v-slot:[`item.Dep_Name`]="{ item }">
              <v-text-field
                dense
                hide-details="auto"
                v-if="item.onEdit"
                v-model="item.Dep_Name"
                :rules="[rules.englishOnly]"
                @input="validateCyrillic(item)"
              ></v-text-field>
              <span v-else>{{ item.Dep_Name }}</span>
            </template>
            <template v-slot:[`item.Is_Active`]="{ item }">
              <v-checkbox
                dense
                hide-details
                class="ml-8"
                v-model="item.Is_Active"
                :readonly="!item.onEdit"
              ></v-checkbox>
            </template>
            <template v-slot:[`item.Can_Delete`]="{ item }">
              <v-btn
                color="success"
                small
                icon
                v-if="item.onEdit"
                :disabled="item.hasCyrillic"
                @click="updateDepName(item)"
              >
                <v-icon>mdi-check-circle</v-icon>
              </v-btn>
              <v-btn
                :disabled="editMode && !item.onEdit"
                color="primary"
                small
                icon
                @click="toggleEditItem(item)"
              >
                <v-icon>
                  {{ item.onEdit ? 'mdi-close-circle' : 'mdi-pencil-circle' }}
                </v-icon>
              </v-btn>
              <v-btn
                color="red darken-2"
                small
                icon
                v-if="!item.onEdit"
                :disabled="!item.Can_Delete || editMode"
                @click="toggleDeleteDialog(item.Dep_ID)"
              >
                <v-icon>mdi-delete-circle</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            small
            @click="createNewDepartmentDialog = !createNewDepartmentDialog"
          >
            Create New
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      max-width="500"
      v-model="createNewDepartmentDialog"
    >
      <v-card>
        <v-card-title>
          New Department
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click.prevent="createNewDepartmentDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-3">
          <v-row>
            <v-col cols="12" md="10" lg="10">
              <v-text-field
                dense
                hide-details="auto"
                label="Department Name"
                counter="50"
                autofocus
                :rules="rules50.concat([rules.englishOnly])"
                v-model="newDepName"
              ></v-text-field>
            </v-col>
            <v-col class="pt-0" cols="12" md="2" lg="2">
              <h5>Is Active</h5>
              <v-checkbox
                class="mt-0 mx-3"
                v-model="newDepActive"
              ></v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            small
            color="primary"
            :disabled="!newDepName || newDepName.length > 50 || /[а-яА-ЯЁёҐґЄєІіЇї]/.test(newDepName)"
            @click="createNewDepartment"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <ConfirmOrDeniedDialog
      v-if="deleteDepartmentDialog"
      v-model="deleteDepartmentDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      @onDeleteClick="deleteDepartment"
      @onCancelClick="cancelDeleteStatus"
      @onCloseClick="cancelDeleteStatus"
    />
  </div>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
export default {
  name: 'DepartmentsManager',
  components: {
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      departments: [],
      previousDepName: [],
      search: '',
      createNewMode: false,
      editMode: false,
      departmentIDToDelete: null,
      deleteDepartmentDialog: false,
      newDepName: '',
      newDepActive: true,
      rules: {
        englishOnly: value => {
          if (!value) return true
          const hasCyrillic = /[а-яА-ЯЁёҐґЄєІіЇї]/.test(value)
          return !hasCyrillic || 'English only'
        }
      },
      rules50: [
        v => !v || v.length <= 50 || 'Max 50 characters'
      ],
      tableHeaders: [
        {
          text: 'Department Name',
          value: 'Dep_Name',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: 'Is Active',
          value: 'Is_Active',
          divider: true,
          class: 'px-2 text-no-wrap',
          cellClass: 'px-2 text-no-wrap'
        },
        {
          text: '',
          width: 100,
          align: 'center',
          sortable: false,
          value: 'Can_Delete'
        }
      ]
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    show: {
      get () {
        if (this.value) {
          this.getDepartments()
        }
        return this.value
      },
      set (value) {
        this.updateDepartments()
        this.$emit('input', value)
      }
    },
    createNewDepartmentDialog: {
      get () {
        return this.createNewMode
      },
      set (value) {
        if (!value) {
          this.newDepName = ''
          this.newDepActive = true
        }
        this.createNewMode = value
      }
    }
  },
  methods: {
    validateCyrillic (item) {
      item.hasCyrillic = /[а-яА-ЯЁёҐґЄєІіЇї]/.test(item.Dep_Name)
    },
    toggleDeleteDialog (departmentID) {
      this.departmentIDToDelete = departmentID
      this.deleteDepartmentDialog = true
    },
    cancelDeleteStatus () {
      this.departmentIDToDelete = null
      this.deleteDepartmentDialog = false
    },
    createNewDepartment () {
      this.axios.post('/api/post_department', {
        Dep_Name: this.newDepName,
        Is_Active: this.newDepActive
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.createNewDepartmentDialog = false
        this.getDepartments()
        this.$store.dispatch('setSuccess', { successMsg: 'New department successfully created' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateDepName (item) {
      this.axios.put('/api/put_department', {
        Dep_ID: item.Dep_ID,
        Dep_Name: item.Dep_Name,
        Is_Active: item.Is_Active
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        const ind = this.previousDepName.findIndex(psd => psd.Dep_ID === item.Dep_ID)
        this.previousDepName[ind].Dep_Name = item.Dep_Name
        this.previousDepName[ind].Is_Active = item.Is_Active
        item.onEdit = false
        this.editMode = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
        item.onEdit = false
      })
    },
    toggleEditItem (item) {
      item.onEdit = !item.onEdit
      this.editMode = !this.editMode
      if (!item.onEdit) {
        const ind = this.previousDepName.findIndex(psd => psd.Dep_ID === item.Dep_ID)
        item.Dep_Name = this.previousDepName[ind].Dep_Name
        item.Is_Active = this.previousDepName[ind].Is_Active
        item.hasCyrillic = false
      }
    },
    getDepartments () {
      this.axios.get('/api/get_departments_to_manage', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.departments = res.data
        this.previousDepName = []
        for (let i = 0; i < this.departments.length; i++) {
          this.$set(this.departments[i], 'onEdit', false)
          this.$set(this.departments[i], 'hasCyrillic', false)
          this.previousDepName.push({
            Dep_ID: this.departments[i].Dep_ID,
            Dep_Name: this.departments[i].Dep_Name,
            Is_Active: this.departments[i].Is_Active
          })
        }
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteDepartment () {
      this.axios.delete(`/api/delete_department/${this.departmentIDToDelete}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getDepartments()
        this.cancelDeleteStatus()
        this.$store.dispatch('setSuccess', { successMsg: 'Department successfully deleted' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    updateDepartments () {
      this.axios.get('/api/department', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.$store.dispatch('setDepartments', { departments: res.data })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  }
}
</script>
